// SWR-driven inbox + mark-read actions. Layout consumes the returned
// adapter shape directly so AdminHeader stays unaware of the API call.
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { getModuleUrl, useTenant } from "@dehub/theme"
import useSWR from "swr"
import { useTranslation } from "react-i18next"
import { NotificationApi } from "../../../apis"
import { message } from "../../../utils/message"
import { mapNotif, MappedNotif, NotifRow, notifHref } from "../notifications-adapter"
import { notifTarget } from "../notif-target"

interface NotifResponse {
  rows: NotifRow[]
  count: number
  /** Inbox-wide unread total (not just the loaded page). */
  unseen?: number
}

export interface NotificationsState {
  notifications: MappedNotif[]
  /** Unread across the whole inbox. */
  unseen: number
  onMarkAllNotifsRead: () => Promise<void>
  /** Mark one notification read without opening it. */
  onMarkRead: (id: string | number) => Promise<void>
  onNotificationClick: (n: { id: string | number; unread?: boolean }) => Promise<void>
}

export function useNotifications(opts: {
  accessToken: string | null | undefined
  hasUser: boolean
  isBuyer?: boolean
}): NotificationsState {
  const navigate = useNavigate()
  const { t } = useTranslation("layout")
  const modulesDomain = useTenant()?.modulesDomain
  // Gate on accessToken + user so we never fire `/2fa/auth/fcm/list`
  // before /access has bootstrapped — a stale token would 401 and the
  // http-util's handler would force-redirect to auth-admin on first paint.
  const enabled = !!opts.accessToken && opts.hasUser

  const { data, mutate } = useSWR<NotifResponse>(
    enabled ? "/2fa/auth/fcm/list" : null,
    () => NotificationApi.list({ offset: { page: 1, limit: 20 } }),
    // Poll as a fallback for browsers where push is blocked or the token is not registered yet.
    { fallbackData: { rows: [], count: 0 }, revalidateOnFocus: false, refreshInterval: 60_000 },
  )

  const notifications = useMemo(
    () => (data?.rows ?? []).map(mapNotif),
    [data],
  )

  const unseen = data?.unseen ?? notifications.filter((n) => n.unread).length

  // B45/B46: one bulk request for the whole inbox. Until `PUT /2fa/auth/fcm/seen-all`
  // is deployed everywhere, a failure falls back to one seen call per loaded unread
  // row (the previous behaviour); only when that fails too is the error shown.
  const onMarkAllNotifsRead = async () => {
    try {
      await NotificationApi.seenAll()
    } catch {
      try {
        await Promise.all(notifications.filter((n) => n.unread).map((n) => NotificationApi.seen(`${n.id}`)))
      } catch {
        message.error(t("notif.markAllFailed"))
      }
    }
    await mutate()
  }

  const onMarkRead = async (id: string | number) => {
    try {
      await NotificationApi.seen(`${id}`)
      await mutate()
    } catch {
      message.error(t("notif.markReadFailed"))
    }
  }

  const onNotificationClick = async (n: { id: string | number; unread?: boolean }) => {
    const full = notifications.find((x) => `${x.id}` === `${n.id}`) ?? (n as unknown as MappedNotif)
    const href = full ? notifHref(full, !!opts.isBuyer) : null
    if (href) {
      navigate(href)
    } else {
      const target = full ? notifTarget(full.objectType, full.objectId, !!opts.isBuyer) : null
      if (target) {
        if (target.module === "order") navigate(target.path)
        else if (opts.accessToken) window.location.href = `${getModuleUrl(target.module, modulesDomain)}/access?token=${encodeURIComponent(opts.accessToken)}&redirect=${encodeURIComponent(target.path)}`
      }
    }
    try {
      if (n.unread) {
        await NotificationApi.seen(`${n.id}`)
        mutate()
      }
    } catch {
      // silent
    }
  }

  return { notifications, unseen, onMarkAllNotifsRead, onMarkRead, onNotificationClick }
}
